import { makeCygnusApiCall } from "api/api";
import { ClientDocumentItem } from "./ClientDocumentsPage.types";

/**
 * API Service for ClientDocuments
 */

/**
 * Fetches all the client documents for the particular clientmasterid.
 * @param {string} clientMasterId - The ID of the client to fetch data for.
 */
export const getDocumentsById = async (clientMasterId: any): Promise<ClientDocumentItem[]> => {
  const allDocuments = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/documents/${clientMasterId}/`,
    show_message: false,
    customized_success_message: "Fetched Documents Data Successfully",
    customized_failure_message: "Unable to fetch documents data, please contact support and attach a screenshot.",
    isblob: false,
    path: "/tax",
    istax: true
  })

  return allDocuments?.data ?? [];
}

/**
 * @param {string} documentId - The ID of the document fetch data for.
 * Fetches the client documents in form of blob to download based on form responsedataid.
 */
export const downloadDocument = async (documentId: any, documentType: any): Promise<Blob> => {
  const document = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: documentType === "Schedule K-1" ? `/k1/download/${documentId}/` : `/download/${documentId}/`,
    show_message: false,
    customized_success_message: "Got Document Data Successfully",
    customized_failure_message: "Unable to fetch document data, please contact support and attach a screenshot.",
    isblob: true,
    path: "/tax",
    istax: true
  });

  return document ?? null;
}


/**
 * @param {string} clientid - The ID of the client to fetch data for.
 * Fetches the return ids of clients.
 */
export const getReturnIds = async (clientMasterId: any): Promise<any[]> => {
  const returnids = await makeCygnusApiCall({
    type: "GET",
    url: `/returns/${clientMasterId}/`,
    show_message: false,
    customized_success_message: "Fetched Data Successfully",
    customized_failure_message: "Unable to fetch data, please contact support and attach a screenshot.",
    isblob: false,
    path: "/tax",
    istax: true
  });
  return returnids?.data ?? [];
}

/**
 * Fetches all the pbc client documents for the particular clientmasterid.
 * @param {string} clientMasterId - The ID of the client to fetch data for.
 */
export const getPBCDocumentsById = async (clientMasterId: any, returnId:any): Promise<any> => {
  const allPBCDocuments = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/documents/pbc/${clientMasterId}/${returnId}/`,
    show_message: false,
    customized_success_message: "Fetched PBC Documents Data Successfully",
    customized_failure_message: "Unable to fetch PBC documents data, please contact support and attach a screenshot.",
    isblob: false,
    path: "/tax",
    istax: true
  })

  if(allPBCDocuments) {
    return { 
      data: allPBCDocuments?.data, 
      flowOrchestratorId: allPBCDocuments?.FlowOrchestratorId, 
      flowOrchestratorIdExcelImport: allPBCDocuments?.FlowOrchestratorIdExcelImport,
      selectedParents: allPBCDocuments?.SelectedParents,
      activityMapping: allPBCDocuments?.ActivityMapping, 
    }  
  }
  return [];
}

/**
 * Imports all the client PBC documents data to CCH.
 * @param {object} payload - all documents data of client.
 */
export const importAllDocumentsToTax = async (
  payload: any,
  options: { showMessage?: boolean } = {},
): Promise<any> => {
  const importToTaxResponse = await makeCygnusApiCall({
    type: "POST",
    payload: payload,
    url: `/import_to_tax/`,
    show_message: options.showMessage ?? true,
    customized_success_message:"Import to CCH Successful",
    customized_failure_message: "Import to CCH Unsuccessful",
    customized_error_message: "Import to CCH Unsuccessful",
    default_message: true,
    isblob: false,
    path: "/tax",
    istax: true
  })

  return importToTaxResponse ?? false;
}

/**
 * Confirms edited preview data and imports it to CCH.
 * @param {object} payload - ActivityId and edited PreTransferTieout data.
 */
export const confirmImportToCCH = async (payload: any): Promise<any> => {
  const confirmImportResponse = await makeCygnusApiCall({
    type: "POST",
    payload: payload,
    url: `/confirm_import_to_tax/`,
    show_message: true,
    customized_success_message: "Import to CCH Successful",
    customized_failure_message: "Import to CCH Unsuccessful",
    customized_error_message: "Import to CCH Unsuccessful",
    default_message: true,
    isblob: false,
    path: "/tax",
    istax: true
  })

  return confirmImportResponse ?? false;
}
